// Google Analytics 4 utility functions for tracking events and page views
// Also includes helpers for dual tracking to both GA and Intercom

import { intercomService, IntercomEvents } from "../services/intercomService";
import type { IntercomEventName } from "../services/intercomService";
import { trackingService } from "../services/trackingService";
import { TrackingEvents } from "../services/trackingEvents";
import { analyticsService } from "../services/analyticsService";
import { isCustomerViewBootProfile } from "./adminCustomerViewBoot";

const gtag = (...args: unknown[]): void => {
  analyticsService.emitSanitizedGtag(...args);
};

// Track page views
export const trackPageView = (url: string, title?: string): void => {
  gtag("event", "page_view", {
    page_path: url,
    page_title: title,
  });
};

// Event parameters interface
interface EventParameters {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: any;
}

// Track custom events
export const trackEvent = (eventName: string, parameters: EventParameters = {}): void => {
  gtag("event", eventName, {
    event_category: parameters.category || "engagement",
    event_label: parameters.label,
    value: parameters.value,
    ...parameters,
  });
};

// Track user authentication events
export const trackAuth = (action: string, method: string = "email"): void => {
  gtag("event", action, {
    event_category: "auth",
    method: method,
  });
};

// Track navigation events
export const trackNavigation = (destination: string, source: string = "unknown"): void => {
  gtag("event", "navigation", {
    event_category: "navigation",
    event_label: `${source} -> ${destination}`,
    page_path: destination,
  });
};

// Track portfolio interactions
export const trackPortfolio = (action: string, portfolioId?: string | null, portfolioName?: string | null): void => {
  gtag("event", action, {
    event_category: "portfolio",
    event_label: portfolioName,
    portfolio_id: portfolioId,
  });
};

// Track dashboard interactions
export const trackDashboard = (action: string, section?: string | null): void => {
  gtag("event", action, {
    event_category: "dashboard",
    event_label: section,
  });
};

// Track button clicks and interactions
export const trackClick = (buttonName: string, location: string = "unknown"): void => {
  gtag("event", "click", {
    event_category: "interaction",
    event_label: `${buttonName} - ${location}`,
  });
};

// Track form submissions
export const trackFormSubmission = (formName: string, success: boolean = true): void => {
  gtag("event", "form_submit", {
    event_category: "form",
    event_label: formName,
    success: success,
  });
};

// Track errors
export const trackError = (errorMessage: string, location: string = "unknown"): void => {
  gtag("event", "exception", {
    description: errorMessage,
    fatal: false,
    event_category: "error",
    event_label: location,
  });
};

// Extract UTM parameters from URL
export const extractUTMParams = (): Record<string, string> => {
  if (isCustomerViewBootProfile()) return {};
  const params: Record<string, string> = {};
  const urlParams = new URLSearchParams(window.location.search);

  const utmKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  utmKeys.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      params[key] = value;
    }
  });

  return params;
};

// Track shareable link visits with UTM parameters.
//
// Fans out to BOTH Google Analytics (legacy) and PostHog via
// trackingService. The router-level `share_link_visit` fires earlier,
// at link-resolution time (anonymous, pre-gate) -- this page-level
// `share_link_resource_view` fires once the resource is actually
// loaded for render, so it carries richer metadata (resource id,
// resource name, provider name, UTM) that the router does not have.
// Together they model the /share and /picks funnels consistently in
// PostHog without losing GA continuity.
//
// NOTE: resourceName and providerName are product-level labels, not
// user PII; Plutus is B2B and product names are public.
export const trackShareableLinkVisit = (
  resourceType: "BASKET" | "PORTFOLIO",
  resourceId: number,
  resourceName: string,
  providerName: string
): void => {
  const utmParams = extractUTMParams();

  gtag("event", "shareable_link_visit", {
    event_category: "shareable_link",
    event_label: resourceName,
    resource_type: resourceType.toLowerCase(),
    resource_id: resourceId,
    provider_name: providerName,
    ...utmParams,
  });

  trackingService.track(TrackingEvents.SHARE_LINK_RESOURCE_VIEW, {
    resource_type: resourceType.toLowerCase(),
    resource_id: resourceId,
    resource_name: resourceName,
    provider_name: providerName,
    ...utmParams,
  });
};

// Dual tracking helper - sends events to both GA and Intercom
// This ensures consistent tracking across both platforms when GA-Intercom integration is enabled
export interface DualTrackingEventParams {
  eventName: string;
  intercomEventName?: IntercomEventName;
  category?: string;
  label?: string;
  value?: number;
  metadata?: Record<string, unknown>;
}

/**
 * Track an event to both Google Analytics and Intercom
 * Use this for high-value user actions that should be tracked in both systems
 */
export const trackDualEvent = (params: DualTrackingEventParams): void => {
  const { eventName, intercomEventName, category, label, value, metadata } = params;

  // Track to Google Analytics
  gtag("event", eventName, {
    event_category: category || "engagement",
    event_label: label,
    value: value,
    ...metadata,
  });

  // Track to Intercom if an Intercom event name is provided
  if (intercomEventName) {
    intercomService.trackEvent(intercomEventName, {
      ga_event_name: eventName,
      category,
      label,
      value,
      ...metadata,
    });
  }
};

/**
 * Track portfolio view to both GA and Intercom
 */
export const trackPortfolioViewDual = (
  portfolioId: number,
  portfolioName?: string,
  providerName?: string,
  providerId?: number
): void => {
  // Track to GA
  trackPortfolio("view_portfolio", portfolioId.toString(), portfolioName);

  // Track to Intercom
  intercomService.trackPortfolioView({
    portfolioId,
    portfolioName,
    providerName,
    providerId,
  });
};

/**
 * Track basket view to both GA and Intercom
 */
export const trackBasketViewDual = (
  basketId: number,
  basketName?: string,
  providerName?: string,
  providerId?: number
): void => {
  // Track to GA
  trackPortfolio("view_basket", basketId.toString(), basketName);

  // Track to Intercom
  intercomService.trackBasketView({
    basketId,
    basketName,
    providerName,
    providerId,
  });
};

/**
 * Track questionnaire completion to both GA and Intercom
 */
export const trackQuestionnaireCompleteDual = (category: string, questionCount: number): void => {
  // Track to GA
  trackFormSubmission(`questionnaire_${category}`, true);

  // Track to Intercom
  intercomService.trackQuestionnaireCompleted({
    category,
    questionCount,
  });
};

// Analytics utilities object for default export
const analytics = {
  trackPageView,
  trackEvent,
  trackAuth,
  trackNavigation,
  trackPortfolio,
  trackDashboard,
  trackClick,
  trackFormSubmission,
  trackError,
  extractUTMParams,
  trackShareableLinkVisit,
  // Dual tracking helpers
  trackDualEvent,
  trackPortfolioViewDual,
  trackBasketViewDual,
  trackQuestionnaireCompleteDual,
  // Re-export Intercom events for convenience
  IntercomEvents,
};

export default analytics;
